import {ReactElement, ReactNode, useEffect, useState} from 'react';
import clsx from 'clsx';
import {AnimatePresence, m} from 'framer-motion';
import {FocusScope} from '@react-aria/focus';
import {createPortal} from 'react-dom';
import {useLocation} from 'react-router-dom';
import {useAuth} from '@common/auth/use-auth';
import {NotificationDialogTrigger} from '@common/notifications/dialog/notification-dialog-trigger';
import {useCustomMenu} from '@common/menus/use-custom-menu';
import {createSvgIconFromTree} from '@common/icons/create-svg-icon';
import {Trans} from '@common/i18n/trans';
import {IconButton} from '@common/ui/buttons/icon-button';
import {useNavigate} from '@common/utils/hooks/use-navigate';
import {useIsDarkMode} from '@common/ui/themes/use-is-dark-mode';
import {CustomMenu} from '@common/menus/custom-menu';
import {useSettings} from '@common/core/settings/use-settings';
import {ButtonColor} from '@common/ui/buttons/get-shared-button-style';
import {MenuIcon} from '@common/icons/material/Menu';
import {MenuItemConfig} from '@common/core/settings/settings';
import {
  NavbarAuthUser,
  NavbarAuthUserProps,
} from '@common/ui/navigation/navbar/navbar-auth-user';
import {useDarkThemeVariables} from '@common/ui/themes/use-dark-theme-variables';
import {Logo} from '@common/ui/navigation/navbar/logo';
import {useLightThemeVariables} from '@common/ui/themes/use-light-theme-variables';
import {isAbsoluteUrl} from '@common/utils/urls/is-absolute-url';
import {CloseIcon} from '@common/icons/material/Close';
import {ChevronRightIcon} from '@common/icons/material/ChevronRight';
import {Underlay} from '@common/ui/overlays/underlay';
import {rootEl} from '@common/core/root-el';

type NavbarColor = 'primary' | 'bg' | 'bg-alt' | 'transparent' | string;

export interface NavbarProps {
  hideLogo?: boolean | null;
  toggleButton?: ReactElement;
  children?: ReactNode;
  className?: string;
  color?: NavbarColor;
  bgOpacity?: number | string;
  darkModeColor?: NavbarColor;
  logoColor?: 'dark' | 'light';
  textColor?: string;
  primaryButtonColor?: ButtonColor;
  border?: string;
  size?: 'xs' | 'sm' | 'md';
  rightChildren?: ReactNode;
  menuPosition?: string;
  authMenuItems?: NavbarAuthUserProps['items'];
  alwaysDarkMode?: boolean;
  wrapInContainer?: boolean;
}
export function Navbar(props: NavbarProps) {
  let {
    hideLogo,
    toggleButton,
    children,
    className,
    border,
    size = 'md',
    color,
    textColor,
    darkModeColor,
    rightChildren,
    menuPosition,
    logoColor,
    authMenuItems,
    alwaysDarkMode = false,
    wrapInContainer = false,
  } = props;
  const isDarkMode = useIsDarkMode() || alwaysDarkMode;
  const {notifications} = useSettings();
  const {isLoggedIn} = useAuth();
  const darkThemeVars = useDarkThemeVariables();
  const lightThemeVars = useLightThemeVariables();
  const showNotifButton = isLoggedIn && notifications?.integrated;
  color = color ?? lightThemeVars?.['--be-navbar-color'] ?? 'primary';
  darkModeColor =
    darkModeColor ?? darkModeColor?.['--be-navbar-color'] ?? 'bg-alt';

  if (isDarkMode) {
    color = darkModeColor;
  }

  return (
    <div
      style={alwaysDarkMode ? darkThemeVars : undefined}
      className={clsx(
        getColorStyle(color, textColor),
        size === 'md' && 'h-64 py-8',
        size === 'sm' && 'h-54 py-4',
        size === 'xs' && 'h-48 py-4',
        border,
        className,
      )}
    >
      <div
        className={clsx(
          'flex h-full items-center justify-end gap-10 pl-14 pr-8 md:pl-20 md:pr-20',
          wrapInContainer && 'container mx-auto',
        )}
      >
        {!hideLogo && (
          <Logo isDarkMode={isDarkMode} color={color} logoColor={logoColor} />
        )}
        {toggleButton}
        {children}
        <MobileMenu position={menuPosition} />
        <DesktopMenu position={menuPosition} />
        <div className="ml-auto flex items-center gap-4 md:gap-14">
          {rightChildren}
          {showNotifButton && <NotificationDialogTrigger />}
          {isLoggedIn && <NavbarAuthUser items={authMenuItems} />}
        </div>
      </div>
    </div>
  );
}

interface DesktopMenuProps {
  position: NavbarProps['menuPosition'];
}
function DesktopMenu({position}: DesktopMenuProps) {
  return (
    <CustomMenu
      className="mx-14 text-sm max-md:hidden"
      itemClassName={isActive =>
        clsx(
          'opacity-90 hover:underline hover:opacity-100',
          isActive && 'opacity-100',
        )
      }
      menu={position}
    />
  );
}

interface MobileMenuProps {
  position: NavbarProps['menuPosition'];
}
function MobileMenu({position}: MobileMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const menu = useCustomMenu(position);
  const darkThemeVars = useDarkThemeVariables();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!menu?.items.length) {
    return null;
  }

  const handleItemClick = (item: MenuItemConfig) => {
    setIsOpen(false);
    if (isAbsoluteUrl(item.action)) {
      window.open(item.action, item.target)?.focus();
    } else {
      navigate(item.action);
    }
  };

  return (
    <>
      <IconButton
        className="border border-white/10 bg-white/5 text-white hover:bg-white/10 md:hidden"
        aria-label="Open navigation menu"
        aria-expanded={isOpen}
        aria-controls="mobile-navigation-drawer"
        onClick={() => setIsOpen(true)}
      >
        <MenuIcon />
      </IconButton>

      {rootEl &&
        createPortal(
          <AnimatePresence>
            {isOpen && (
              <div
                className="fixed inset-0 isolate z-modal md:hidden"
                style={darkThemeVars}
                onKeyDown={event => {
                  if (event.key === 'Escape') {
                    event.stopPropagation();
                    setIsOpen(false);
                  }
                }}
              >
                <Underlay
                  className="bg-black/75"
                  onClick={() => setIsOpen(false)}
                />
                <m.aside
                  id="mobile-navigation-drawer"
                  className="absolute inset-y-0 right-0 z-20 flex w-[88vw] max-w-350 flex-col border-l border-white/10 bg-background text-main shadow-2xl"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Navigation menu"
                  initial={{x: '100%'}}
                  animate={{x: 0}}
                  exit={{x: '100%'}}
                  transition={{type: 'tween', duration: 0.22}}
                >
                  <FocusScope restoreFocus autoFocus contain>
                    <div className="flex h-64 flex-shrink-0 items-center border-b border-white/10 px-16">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                          <Trans message="Myagasta" />
                        </div>
                        <div className="mt-2 text-lg font-semibold text-white">
                          <Trans message="Browse" />
                        </div>
                      </div>
                      <IconButton
                        className="ml-auto border border-white/10 bg-white/5 text-white hover:bg-white/10"
                        aria-label="Close navigation menu"
                        onClick={() => setIsOpen(false)}
                      >
                        <CloseIcon />
                      </IconButton>
                    </div>

                    <nav
                      className="flex-auto overflow-y-auto overscroll-contain px-12 py-16"
                      aria-label="Primary navigation"
                    >
                      <div className="flex flex-col gap-4">
                        {menu.items.map(item => {
                          const Icon =
                            item.icon && createSvgIconFromTree(item.icon);
                          const isActive =
                            item.action === '/'
                              ? location.pathname === '/'
                              : location.pathname === item.action ||
                                location.pathname.startsWith(`${item.action}/`);

                          return (
                            <button
                              type="button"
                              className={clsx(
                                'group flex min-h-52 w-full items-center gap-12 rounded-xl px-14 text-left text-base font-medium outline-none transition',
                                'focus-visible:ring-2 focus-visible:ring-primary',
                                isActive
                                  ? 'bg-primary/15 text-primary'
                                  : 'text-white/80 hover:bg-white/5 hover:text-white',
                              )}
                              onClick={() => handleItemClick(item)}
                              key={item.id}
                              aria-current={isActive ? 'page' : undefined}
                            >
                              <span
                                className={clsx(
                                  'flex h-36 w-36 flex-shrink-0 items-center justify-center rounded-lg',
                                  isActive
                                    ? 'bg-primary/20 text-primary'
                                    : 'bg-white/5 text-white/60 group-hover:bg-white/10 group-hover:text-white',
                                )}
                              >
                                {Icon ? (
                                  <Icon size="sm" />
                                ) : (
                                  <span className="h-6 w-6 rounded-full bg-current" />
                                )}
                              </span>
                              <span className="min-w-0 flex-auto truncate">
                                <Trans message={item.label} />
                              </span>
                              <ChevronRightIcon
                                size="sm"
                                className="flex-shrink-0 opacity-40 transition-transform group-hover:translate-x-2 group-hover:opacity-100"
                              />
                            </button>
                          );
                        })}
                      </div>
                    </nav>

                    <div className="flex-shrink-0 border-t border-white/10 px-16 py-14 text-xs uppercase tracking-wider text-white/40">
                      <Trans message="Movies • TV Shows • Streaming" />
                    </div>
                  </FocusScope>
                </m.aside>
              </div>
            )}
          </AnimatePresence>,
          rootEl,
        )}
    </>
  );
}

function getColorStyle(color: string, textColor?: string): string {
  switch (color) {
    case 'primary':
      return `bg-primary ${textColor || 'text-on-primary'} border-b-primary`;
    case 'bg':
      return `bg ${textColor || 'text-main'} border-b`;
    case 'bg-alt':
      return `bg-alt ${textColor || 'text-main'} border-b`;
    case 'transparent':
      return `bg-transparent ${textColor || 'text-white'}`;
    default:
      return `${color} ${textColor}`;
  }
}
